import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
} from 'lucide-react';
import { useEffect, useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { Direcao, PaginaTabela } from './tabela-tipos';
import type { Visao } from './alternador-visao';
import { CONTROLE_BARRA, Selecao } from './ui';

export interface Coluna<T> {
  chave: string;
  titulo: string;
  celula: (linha: T) => ReactNode;
  /** Colunas secundárias somem no celular em vez de espremer a tabela. */
  ocultarNoCelular?: boolean;
  alinhamento?: 'esquerda' | 'direita';
  /**
   * Ordenável pelo cabeçalho. Só marque quando o SERVIDOR souber ordenar por
   * ela: com paginação no servidor, ordenar em memória ordenaria só a página
   * carregada — a tabela pareceria ordenada e o primeiro item da lista inteira
   * poderia estar na página 7.
   */
  ordenavel?: boolean;
  /**
   * Papel da coluna no CARD.
   *
   * O card não é a linha empilhada. `titulo` identifica o registro e vai em
   * cima; `destaque` é o número que se procura — o total — e fica no canto; o
   * resto vira par rótulo/valor. Sem esse papel o card sairia com oito
   * "Rótulo: valor" iguais e nenhum ponto de entrada para o olho.
   *
   * Nada marcado: a PRIMEIRA coluna vira o título, que é como as tabelas daqui
   * já estão montadas — a coluna que identifica sempre vem antes.
   */
  papel?: 'titulo' | 'destaque';
}

export interface Ordenacao {
  chave: string;
  direcao: Direcao;
}

interface TabelaProps<T> {
  colunas: Coluna<T>[];
  pagina: PaginaTabela<T> | undefined;
  carregando: boolean;
  erro?: string | undefined;
  aoTrocarPagina: (pagina: number) => void;
  /** Passe para o rodapé oferecer "por página". Sem isto, o seletor não sai. */
  aoTrocarTamanho?: (tamanho: number) => void;
  /** Slot do rodape: total somado, exportar. A tela sabe o que somar. */
  rodape?: ReactNode;
  aoClicarLinha?: (linha: T) => void;
  vazio: { titulo: string; descricao: string; acao?: ReactNode };
  chaveDe: (linha: T) => string;
  ordenacao?: Ordenacao | null;
  aoOrdenar?: (ordenacao: Ordenacao | null) => void;
  /**
   * Menu de ações da linha, na última coluna. O clique dentro dele NÃO abre a
   * linha — senão escolher "Desativar" abriria a edição junto.
   */
  acoes?: (linha: T) => ReactNode;
  /**
   * Lista (padrão) ou cards, vindo de `useVisao` — que lembra a escolha POR
   * TELA. As duas leituras existem: a lista compara valores entre registros, o
   * card identifica um registro. Quem confere faturamento quer lista; quem
   * procura um pedido específico quer card.
   */
  visao?: Visao;
  /**
   * Card SOB MEDIDA para esta tela, no lugar do genérico.
   *
   * O card derivado das colunas serve quando a linha é uma linha: rótulo,
   * valor, rótulo, valor. Não serve quando o registro tem HIERARQUIA — no
   * pedido, o cliente é a manchete, o total é o fecho e "Situação:" antes do
   * selo é uma palavra que não informa nada. Forçar isso no genérico
   * distorceria o componente para as outras nove telas.
   *
   * Recebe as ações já montadas para a tela não ter de declará-las duas vezes.
   */
  cartao?: (linha: T, acoes: ReactNode | null) => ReactNode;
}

/**
 * Tabela do administrativo.
 *
 * Os três estados — carregando, erro e vazio — são obrigatórios, não enfeite:
 * tela que só sabe desenhar a lista cheia deixa o usuário olhando para o nada
 * quando o filtro não acha nada, e ele conclui que o sistema perdeu o dado.
 */
/**
 * `true` abaixo de `sm` (639px), o mesmo ponto em que o alternador some.
 *
 * ## Por que DOIS eventos
 * O caminho normal é o `change` da media query — ele avisa só quando o limite
 * é cruzado, sem re-renderizar a cada pixel do arrasto. Mas ele nem sempre
 * chega: medido neste projeto, um viewport de 375px em que
 * `matchMedia('(max-width: 639px)').matches` já era `true` e o `change` não
 * havia disparado NENHUMA vez — a tela ficou em tabela onde devia estar em
 * cards.
 *
 * Por isso o `resize` entra junto, como rede. Ele dispara muito, e tudo bem:
 * o efeito só lê `consulta.matches` (booleano já calculado pelo navegador, sem
 * medir layout) e chama `setEstreita` com o MESMO valor na maioria das vezes —
 * e React não re-renderiza quando o estado não muda. O custo é uma comparação
 * por evento.
 *
 * ## O guard de `matchMedia`
 * Não é zelo excessivo: o jsdom tem `window` sem `matchMedia`, e sem ele a
 * suíte inteira cai na montagem.
 */
function useTelaEstreita(): boolean {
  const [estreita, setEstreita] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const consulta = window.matchMedia('(max-width: 639px)');
    const aplicar = () => setEstreita(consulta.matches);
    aplicar();
    consulta.addEventListener('change', aplicar);
    window.addEventListener('resize', aplicar);
    return () => {
      consulta.removeEventListener('change', aplicar);
      window.removeEventListener('resize', aplicar);
    };
  }, []);

  return estreita;
}

export function Tabela<T>({
  colunas,
  pagina,
  carregando,
  erro,
  aoTrocarPagina,
  aoTrocarTamanho,
  rodape,
  aoClicarLinha,
  vazio,
  chaveDe,
  ordenacao = null,
  aoOrdenar,
  acoes,
  visao = 'lista',
  cartao,
}: TabelaProps<T>) {
  // Abaixo de `sm` a tabela vira CARDS, qualquer que seja a visão escolhida.
  // Oito colunas num aparelho de 375px só existem atrás de uma rolagem
  // lateral, e rolagem lateral esconde coluna: a pessoa não sabe o que deixou
  // de ver. O alternador some abaixo de `sm` justamente porque ali não há
  // escolha a fazer — só uma leitura serve.
  //
  // `&& cartao`: quem não passou como desenhar o cartão continua na tabela.
  // Forçar cards sem ter cartão renderizaria uma lista vazia.
  const estreita = useTelaEstreita();
  const visaoReal = estreita && cartao ? 'cards' : visao;

  if (erro) {
    return (
      <div className="rounded-xl border border-tinta-100 bg-superficie p-6 text-center">
        <p className="text-erro-600 font-medium">{erro}</p>
        <p className="text-tinta-500 mt-1 text-sm">
          Tente de novo. Se continuar, avise o suporte.
        </p>
      </div>
    );
  }

  if (carregando && !pagina)
    return <Esqueleto colunas={colunas.length} visao={visaoReal} />;

  // Uma linha, não um bloco: "nada aqui" não pode ocupar mais altura do que a
  // TABELA ocuparia com dado — ver a regra de proporção em `pagina-shell.tsx`.
  if (pagina && pagina.total === 0) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-tinta-100 bg-superficie px-3 py-2.5">
        <p className="text-tinta-700 text-sm">
          <span className="text-tinta-900 font-medium">{vazio.titulo}</span>{' '}
          {vazio.descricao}
        </p>
        {vazio.acao && <div className="shrink-0">{vazio.acao}</div>}
      </div>
    );
  }

  if (visaoReal === 'cards') {
    return (
      <div className="space-y-3">
        {/*
          A ordenação vive no CABEÇALHO da tabela — e o card não tem cabeçalho.
          Sem esta barra, trocar para cards tiraria em silêncio a capacidade de
          ordenar, e a pessoa concluiria que a tela perdeu a função.
        */}
        <BarraOrdenacao colunas={colunas} ordenacao={ordenacao} aoOrdenar={aoOrdenar} />

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 xl:grid-cols-3">
          {pagina?.itens.map((linha) =>
            cartao ? (
              <div key={chaveDe(linha)}>{cartao(linha, acoes ? acoes(linha) : null)}</div>
            ) : (
              <Cartao
                key={chaveDe(linha)}
                linha={linha}
                colunas={colunas}
                {...(aoClicarLinha ? { aoClicar: () => aoClicarLinha(linha) } : {})}
                {...(acoes ? { acoes: acoes(linha) } : {})}
              />
            ),
          )}
        </div>

        {pagina && (
          <Paginacao
            pagina={pagina}
            aoTrocarPagina={aoTrocarPagina}
            aoTrocarTamanho={aoTrocarTamanho}
            rodape={rodape}
            classe="rounded-xl border border-tinta-100 bg-superficie"
          />
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-tinta-100 bg-superficie">
      {/* Rolagem horizontal fica DENTRO da caixa: a página nunca rola de lado.
          `relative` ancora os `sr-only` (que são `position:absolute`) — o
          "Ações" do cabeçalho, logo abaixo. Sem ancestral posicionado eles se
          ancoram no VIEWPORT e esticam a área de rolagem do `<html>`: a página
          ganha um segundo scroll. O `overflow-hidden` do pai não os recorta,
          porque o bloco contentor deles não está dentro dele. */}
      <div className="relative overflow-x-auto">
        <table className="w-full text-sm">
          {/* Sem fundo próprio: o thead herda a cor do card e quem separa é a
              LINHA de baixo em força cheia + o peso da fonte. A faixa
              `bg-superficie-2` era uma camada a mais — e no escuro ficava mais
              CLARA que o corpo da tabela. */}
          <thead className="text-tinta-500">
            <tr className="border-tinta-100 border-b">
              {colunas.map((c) => (
                <th
                  key={c.chave}
                  scope="col"
                  aria-sort={
                    ordenacao?.chave === c.chave
                      ? ordenacao.direcao === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : c.ordenavel && aoOrdenar
                        ? 'none'
                        : undefined
                  }
                  className={celulaClasse(
                    c,
                    // Cabeçalho ordenável reage ao mouse com um véu MAIS FRACO
                    // que o da linha: ele fica logo acima dela, e dois véus de
                    // mesma força fariam a tabela inteira parecer selecionada.
                    //
                    // Padding FIXO (não `--pad-y`): a densidade é do CORPO, o
                    // cabeçalho não acompanha. E `font-bold` é o que separa o
                    // cabeçalho do dado agora que a faixa de fundo saiu — o
                    // peso faz o trabalho que a cor fazia. `whitespace-nowrap`
                    // porque título quebrado em duas linhas desalinha a régua
                    // de leitura das colunas vizinhas.
                    `whitespace-nowrap px-3 py-2.5 font-bold ${
                      c.ordenavel && aoOrdenar
                        ? 'hover:bg-marca-600/[0.06] transition-colors'
                        : ''
                    }`,
                  )}
                >
                  {c.ordenavel && aoOrdenar ? (
                    <CabecalhoOrdenavel
                      titulo={c.titulo}
                      direcao={ordenacao?.chave === c.chave ? ordenacao.direcao : null}
                      aoClicar={() => aoOrdenar(proximaOrdem(ordenacao, c.chave))}
                      aDireita={c.alinhamento === 'direita'}
                    />
                  ) : (
                    c.titulo
                  )}
                </th>
              ))}
              {acoes && (
                <th scope="col" className="w-10 px-2 py-2.5">
                  <span className="sr-only">Ações</span>
                </th>
              )}
            </tr>
          </thead>
          {/* Linhas a 60%, moldura e cabeçalho em cor cheia: é a hierarquia do
              isotec. Com os três no mesmo peso a tabela vira grade, não lista —
              e o olho perde a moldura de vista. */}
          <tbody className="divide-y divide-tinta-100/60">
            {pagina?.itens.map((linha) => (
              <tr
                key={chaveDe(linha)}
                onClick={aoClicarLinha ? () => aoClicarLinha(linha) : undefined}
                /*
                  `focus-within` junto do `hover`, e não só o hover.

                  Quem navega por teclado chega na linha pelo botão de ações
                  dela; sem isto, o foco pousa numa linha que não se destaca de
                  nenhuma outra, e a pessoa perde o lugar na tabela.
                */
                className={`hover:bg-marca-600/[0.07] focus-within:bg-marca-600/[0.07] transition-colors ${
                  aoClicarLinha ? 'cursor-pointer' : ''
                }`}
              >
                {colunas.map((c) => (
                  <td key={c.chave} className={celulaClasse(c, 'px-3 py-[var(--pad-y)]')}>
                    {c.celula(linha)}
                  </td>
                ))}
                {acoes && (
                  /*
                    `stopPropagation` no <td>: sem ele o clique em "Desativar"
                    sobe para a linha e abre a edição no mesmo gesto — a pessoa
                    escolhe uma coisa e recebe duas.
                  */
                  <td
                    className="w-10 px-2 py-[var(--pad-y)] text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {acoes(linha)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagina && (
        <Paginacao
          pagina={pagina}
          aoTrocarPagina={aoTrocarPagina}
          aoTrocarTamanho={aoTrocarTamanho}
          rodape={rodape}
          classe="border-t border-tinta-100"
        />
      )}
    </div>
  );
}

/**
 * O ciclo do clique no cabeçalho: crescente, decrescente e SEM ordenação.
 *
 * O terceiro estado existe porque a ordem padrão da lista costuma ser a útil
 * (nome, código), e sem ele não há como voltar para ela a não ser recarregando
 * a tela.
 */
export function proximaOrdem(atual: Ordenacao | null, chave: string): Ordenacao | null {
  if (atual?.chave !== chave) return { chave, direcao: 'asc' };
  if (atual.direcao === 'asc') return { chave, direcao: 'desc' };
  return null;
}

function CabecalhoOrdenavel({
  titulo,
  direcao,
  aoClicar,
  aDireita,
}: {
  titulo: string;
  direcao: Direcao | null;
  aoClicar: () => void;
  aDireita: boolean;
}) {
  const Icone =
    direcao === 'asc' ? ChevronUp : direcao === 'desc' ? ChevronDown : ChevronsUpDown;
  return (
    <button
      type="button"
      onClick={aoClicar}
      className={`group hover:text-tinta-900 -mx-1 flex w-full items-center gap-1 rounded px-1 py-0.5 transition-colors ${
        aDireita ? 'justify-end' : ''
      }`}
    >
      {titulo}
      {/*
        No repouso o chevron duplo fica apagado, não invisível: a afordância de
        "dá pra ordenar aqui" precisa estar na tela sem o mouse em cima.
        Opacidade em vez de esconder mantém o espaço reservado — senão a largura
        da coluna mudaria a cada passada do mouse.
      */}
      <Icone
        aria-hidden
        className={`size-3.5 shrink-0 transition-opacity ${
          direcao ? 'text-marca-600 opacity-100' : 'opacity-40 group-hover:opacity-70'
        }`}
      />
    </button>
  );
}

function celulaClasse<T>(coluna: Coluna<T>, base: string): string {
  return [
    base,
    coluna.alinhamento === 'direita' ? 'text-right' : 'text-left',
    coluna.ocultarNoCelular ? 'hidden md:table-cell' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** As opções de "por página". Curta de propósito: quatro cobrem o uso real. */
const TAMANHOS_DE_PAGINA = [10, 25, 50, 100] as const;

function Paginacao<T>({
  pagina,
  aoTrocarPagina,
  aoTrocarTamanho,
  classe,
  rodape,
}: {
  pagina: PaginaTabela<T>;
  aoTrocarPagina: (p: number) => void;
  /**
   * Sem isto, o seletor de "por página" não aparece — não há o que trocar.
   *
   * `| undefined` explícito por causa de `exactOptionalPropertyTypes`: quem
   * repassa a prop repassa o valor que RECEBEU, que pode ser `undefined`, e
   * sem isto o TypeScript separa "ausente" de "presente e indefinido".
   */
  aoTrocarTamanho?: ((tamanho: number) => void) | undefined;
  /** Slot do total. Domínio não mora aqui — a tela é quem sabe somar o quê. */
  rodape?: ReactNode | undefined;
  /**
   * A moldura muda com a visão: na tabela a paginação é o último bloco DENTRO
   * da caixa e só precisa da linha de cima; no grid de cards ela é uma caixa
   * própria, e herdar a borda de cima ali desenharia duas linhas coladas.
   */
  classe: string;
}) {
  /*
    Botão-ícone cru em vez do `Botao`: com seta e legenda no lugar do texto
    "Anterior"/"Próxima", o rodapé para de disputar largura com o total e cabe
    na mesma faixa da célula. O que o `Botao` dava de graça precisa vir escrito
    à mão aqui — `focus-visible:ring-2` (senão quem navega por teclado perde a
    seta de vista) e `disabled:opacity-40` (senão o desabilitado fica igual ao
    ativo). `enabled:hover:` e não `disabled:pointer-events-none`: o nosso
    desabilitado é informativo e precisa continuar recebendo o mouse.
  */
  const nav =
    'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-tinta-100 bg-campo text-tinta-700 transition-colors enabled:hover:bg-marca-600/[0.12] disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-marca-500 focus-visible:outline-none';

  const tamanho = pagina.tamanho;
  const primeiro = tamanho ? (pagina.pagina - 1) * tamanho + 1 : 1;
  const ultimo = tamanho ? Math.min(pagina.pagina * tamanho, pagina.total) : pagina.total;

  return (
    /*
      Empilha no celular e só vira linha a partir do `sm`: com o slot de total
      no meio, os três blocos lado a lado espremiam a contagem até quebrar.
    */
    <div
      className={`flex flex-col gap-3 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between ${classe}`}
    >
      {/*
        Faixa e "por página" à esquerda, como no isotec.

        A FAIXA ("1–10 de 183") diz duas coisas que a contagem sozinha não
        diz: onde a página começa e quanto falta. Com uma página só ela vira
        "1–1 de 1", que parece redundante mas continua sendo a resposta certa
        para "o filtro pegou o quê?" — e some junto com o resto quando o
        rodapé some, não antes.
      */}
      <div className="flex items-center gap-3">
        <p className="text-tinta-500 text-xs tabular-nums">
          {pagina.total === 0 ? (
            'nenhum registro'
          ) : (
            <>
              {primeiro}–{ultimo} de {pagina.total}
            </>
          )}
        </p>

        {aoTrocarTamanho && (
          <label className="text-tinta-500 flex items-center gap-1.5 text-xs">
            Por página
            {/*
              `<select>` nativo, e não o `SeletorOpcao` premium — a exceção à
              regra do dropdown premium, com motivo: aquele nasce `h-11`, para
              a barra de ferramentas, e aqui a faixa toda tem 32px. O premium
              existe para resolver rótulo longo, ícone e linha de apoio;
              nenhum dos três aparece numa lista de quatro números.
            */}
            <select
              aria-label="Registros por página"
              value={tamanho ?? TAMANHOS_DE_PAGINA[0]}
              onChange={(e) => aoTrocarTamanho(Number(e.target.value))}
              className="border-tinta-100 bg-campo text-tinta-900 focus-visible:ring-marca-500 h-8 rounded-lg border px-2 text-xs tabular-nums focus-visible:ring-2 focus-visible:outline-none"
            >
              {TAMANHOS_DE_PAGINA.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/*
          O total fica ao lado da navegação, não numa linha própria: é a mesma
          pergunta ("o que este filtro me deu?"), e separar em duas linhas faz
          o olho procurar duas vezes.
        */}
        {rodape}

        {/*
          As setas aparecem SEMPRE, desabilitadas quando não há para onde ir.

          Escondê-las com uma página só parecia limpeza, mas o rodapé mudava
          de forma conforme o filtro — a pessoa filtrava, a navegação sumia, e
          o lugar de virar página passava a depender do resultado. Um controle
          que muda de lugar custa mais do que um botão apagado.
        */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Página anterior"
            className={nav}
            disabled={pagina.pagina <= 1}
            onClick={() => aoTrocarPagina(pagina.pagina - 1)}
          >
            <ChevronLeft className="size-4" aria-hidden />
          </button>

          {/* `aria-live` porque a troca de página não move o foco: sem isso o
              leitor de tela clica e não ouve nada. `min-w` reserva o espaço
              para o rodapé não sacudir quando "1" vira "10". */}
          <span
            aria-live="polite"
            className="text-tinta-500 min-w-[5.5rem] text-center text-xs tabular-nums"
          >
            página {pagina.pagina} de {Math.max(1, pagina.totalPaginas)}
          </span>

          <button
            type="button"
            aria-label="Próxima página"
            className={nav}
            disabled={pagina.pagina >= pagina.totalPaginas}
            onClick={() => aoTrocarPagina(pagina.pagina + 1)}
          >
            <ChevronRight className="size-4" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Um registro como CARD.
 *
 * É um `<article>`, e não um `<button>` como no seletor: aqui a linha pode
 * carregar o menu de ações, e botão dentro de botão é HTML inválido — o
 * navegador desfaz o aninhamento e o menu para de abrir pelo teclado. Quando a
 * tela passa `aoClicar`, o card ganha `role`, foco e Enter/Espaço à mão.
 */
function Cartao<T>({
  linha,
  colunas,
  aoClicar,
  acoes,
}: {
  linha: T;
  colunas: Coluna<T>[];
  aoClicar?: () => void;
  acoes?: ReactNode;
}) {
  const titulo = colunas.find((c) => c.papel === 'titulo') ?? colunas[0];
  const destaque = colunas.find((c) => c.papel === 'destaque');
  const demais = colunas.filter((c) => c !== titulo && c !== destaque);
  /*
    Coluna SEM título é coluna de ação — é assim que o portão, a embalagem e o
    fechamento penduram "Consumir", "Embalar" e "Fechar" na ponta da linha.
    Na tabela ela aparece só com a célula, porque o cabeçalho está vazio; no
    card, virar par rótulo/valor produziria um `<dt>` vazio ao lado de um botão.
    Vai inteira no rodapé do card, que é onde a ação é alcançável com o polegar.
  */
  const acoesDeColuna = demais.filter((c) => c.titulo === '');
  const resto = demais.filter((c) => c.titulo !== '');

  return (
    <article
      {...(aoClicar
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: aoClicar,
            onKeyDown: (evento: KeyboardEvent) => {
              if (evento.key === 'Enter' || evento.key === ' ') {
                evento.preventDefault();
                aoClicar();
              }
            },
          }
        : {})}
      className={`border-tinta-100 bg-superficie hover:border-marca-400 hover:bg-marca-600/[0.07] focus-within:border-marca-400 flex flex-col gap-3 rounded-xl border p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        aoClicar ? 'cursor-pointer outline-none' : ''
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="text-tinta-900 min-w-0 flex-1 font-medium">
          {titulo?.celula(linha)}
        </div>
        {destaque && (
          /*
            O destaque leva RÓTULO, ao contrário do que parecia bastar.
            "R$ 9.510,00" se lê sozinho; "3" e "—" não — e foi assim que o
            portão passou a mostrar um travessão solto no canto do card, sem
            dizer que aquilo era o "falta montar". Na tabela o cabeçalho da
            coluna respondia isso; o card não tem cabeçalho.
          */
          <div className="shrink-0 text-right">
            <span className="text-tinta-500 block text-[10px] tracking-wide uppercase">
              {destaque.titulo}
            </span>
            <span className="text-tinta-900 font-semibold tabular-nums">
              {destaque.celula(linha)}
            </span>
          </div>
        )}
        {/* Mesmo motivo do `<td>` da tabela: sem parar o clique aqui, escolher
            "Desativar" no menu abriria a edição do card no mesmo gesto. */}
        {acoes && (
          <div className="-mt-1 -mr-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            {acoes}
          </div>
        )}
      </div>

      {resto.length > 0 && (
        /*
          `ocultarNoCelular` NÃO se aplica aqui: aquela flag existe porque a
          tabela espreme colunas até ficarem ilegíveis, e o card empilha. Some
          na tabela do celular, aparece no card do celular — é o card que
          resolve o problema que a flag contorna.
        */
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          {resto.map((c) => (
            <div
              key={c.chave}
              className="flex min-w-0 items-baseline justify-between gap-2"
            >
              <dt className="text-tinta-500 shrink-0">{c.titulo}</dt>
              <dd className="text-tinta-700 min-w-0 truncate text-right">
                {c.celula(linha)}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {acoesDeColuna.map((c) => (
        <div key={c.chave} className="empty:hidden">
          {c.celula(linha)}
        </div>
      ))}
    </article>
  );
}

/** Ordenação para a visão de cards — ver o comentário de quem a chama. */
function BarraOrdenacao<T>({
  colunas,
  ordenacao,
  aoOrdenar,
}: {
  colunas: Coluna<T>[];
  ordenacao: Ordenacao | null;
  aoOrdenar: ((ordenacao: Ordenacao | null) => void) | undefined;
}) {
  // `useId` e não um id fixo: telas como o painel comercial põem duas tabelas
  // na mesma página, e dois `<label for="ordem">` mandariam os dois cliques
  // para o primeiro select.
  const id = useId();
  const ordenaveis = colunas.filter((c) => c.ordenavel);
  if (!aoOrdenar || ordenaveis.length === 0) return null;

  const Seta = ordenacao?.direcao === 'desc' ? ArrowDown : ArrowUp;

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <label htmlFor={id} className="text-tinta-500 text-xs">
        Ordenar por
      </label>
      {/* Largura pelo CONTAINER: `w-full` mora na base do campo, e disputar
          isso por `className` depende da ordem das regras no CSS gerado. */}
      <div className="w-44">
        <Selecao
          id={id}
          value={ordenacao?.chave ?? ''}
          onChange={(e) =>
            aoOrdenar(e.target.value ? { chave: e.target.value, direcao: 'asc' } : null)
          }
        >
          <option value="">Padrão</option>
          {ordenaveis.map((c) => (
            <option key={c.chave} value={c.chave}>
              {c.titulo}
            </option>
          ))}
        </Selecao>
      </div>
      <button
        type="button"
        disabled={!ordenacao}
        onClick={() =>
          ordenacao &&
          aoOrdenar({
            chave: ordenacao.chave,
            direcao: ordenacao.direcao === 'asc' ? 'desc' : 'asc',
          })
        }
        aria-label={
          ordenacao?.direcao === 'desc' ? 'Ordem decrescente' : 'Ordem crescente'
        }
        title={ordenacao?.direcao === 'desc' ? 'Decrescente' : 'Crescente'}
        className={`${CONTROLE_BARRA} w-11 justify-center disabled:opacity-40`}
      >
        <Seta className="size-4" aria-hidden />
      </button>
    </div>
  );
}

function Esqueleto({ colunas, visao }: { colunas: number; visao: Visao }) {
  if (visao === 'cards') {
    return (
      <div
        className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 xl:grid-cols-3"
        aria-busy
        aria-label="Carregando"
      >
        {Array.from({ length: 6 }, (_, cartao) => (
          <div
            key={cartao}
            className="border-tinta-100 bg-superficie space-y-3 rounded-xl border p-4"
          >
            {/* `tinta-50` e não `tinta-100`: o cinza do esqueleto é fundo, não
                traço. Em `tinta-100` os blocos liam como conteúdo real e o
                cartão parecia carregado com dado errado. */}
            <div className="bg-tinta-50 h-4 w-2/3 animate-pulse rounded" />
            <div className="bg-tinta-50 h-3 w-full animate-pulse rounded" />
            <div className="bg-tinta-50 h-3 w-4/5 animate-pulse rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-xl border border-tinta-100 bg-superficie"
      aria-busy
      aria-label="Carregando"
    >
      {Array.from({ length: 5 }, (_, linha) => (
        /* Mesma altura da linha REAL: com `py-4` fixo o esqueleto era ~9px mais
           alto e a tabela encolhia no instante em que o dado chegava. O `/60`
           acompanha o separador do corpo, e `h-5` fecha os 4px residuais contra
           a entrelinha de 20px do `text-sm`. */
        <div
          key={linha}
          className="flex gap-4 border-b border-tinta-100/60 px-3 py-[var(--pad-y)]"
        >
          {Array.from({ length: colunas }, (_, coluna) => (
            <div
              key={coluna}
              className="bg-tinta-50 h-5 w-full max-w-[8rem] flex-1 animate-pulse rounded"
            />
          ))}
        </div>
      ))}
    </div>
  );
}
